import { useEffect, useRef, useState } from "react";
import { api } from "../../services/apiService";
import { createDriveModeEngine } from "../../services/driveModeEngine";
import {
  createDriveModePlayer,
  REMOTE_COMMANDS,
  STATES,
} from "../../services/driveModePlayer";
import {
  bindDriveRouteChange,
  resolveDriveRoutePlugin,
} from "../../services/driveAudioRoute";
import { isNativeCapacitorRuntime } from "../../services/revenueCatService";
import { driveModeAvailable } from "./drive-mode-flags";
import answerWindowEarcon from "../../assets/drive-mode/answer-window.mp3";
import repromptEarcon from "../../assets/drive-mode/reprompt.mp3";
import "./drive-mode-screen.scss";

const INITIAL_STATE = { state: STATES.LOADING, questionId: null, paused: false };

function optionKey(option, index) {
  return String(option?.key || option?.optionKey || String.fromCodePoint(65 + index)).toUpperCase();
}

/** The REAL answer value: the production option STRING. This mirrors the
 * established exam path (exam-question-container), where `answerGiven` is the
 * selected `optionList` string and `correctAnswer` is compared to the option text.
 * The synthesized A/B/C letter from optionKey() is display/test-id only and must
 * never be submitted as the graded answer. */
function optionValue(option) {
  return option?.text || option?.label || "";
}

/** One glanceable status line derived purely from the player snapshot (no new
 * player API): route-hold and pause take precedence over the phase label so a
 * silent screen after a Bluetooth drop or a manual pause is never mistaken for
 * playback. */
function phaseBanner(snapshot) {
  if (snapshot.routeHeld) return "Audio paused — tap Play to resume";
  if (snapshot.paused) return "Paused — tap Play";
  switch (snapshot.state) {
    case STATES.PLAYING_STEM: return "Playing question…";
    case STATES.PLAYING_OPTIONS: return "Choices…";
    case STATES.AWAITING_ANSWER: return "Choose your answer";
    case STATES.PLAYING_ANSWER:
    case STATES.PLAYING_DEEPER: return "Explanation…";
    default: return "";
  }
}

/** Map an audio manifest to the player's ordered per-phase segment-list shape. */
function manifestToQuestion(item, manifest) {
  if (!manifest || !Array.isArray(manifest.segments)) {
    throw new Error("audio manifest unavailable");
  }
  const byPhase = { stem: [], options: [], answer: [], deeper: [] };
  manifest.segments.forEach((segment) => {
    const phase = segment?.segment === "answerBrief" ? "answer" : segment?.segment;
    if (byPhase[phase] && segment?.url) byPhase[phase].push(segment.url);
  });
  return { ...item, segments: byPhase };
}

/**
 * The bounded audio queue behind Drive Mode: a session-scoped
 * fetch-cache + manifest-prepare + current/next prefetch with eviction, extracted
 * from the screen effect so the nested playback logic is a small set of flat named
 * helpers (unchanged behavior). Returns {@code { next, fetchSegment, dispose }};
 * {@code fetchSegment} is shared with the engine so bytes are fetched once.
 */
function createDriveModeQueue(questions) {
  let index = 0;
  const blobCache = new Map();
  const prepared = new Map();

  const fetchSegment = async (ref) => {
    if (!blobCache.has(ref)) blobCache.set(ref, Promise.resolve(api.getAudioSegmentBlob(ref)));
    return blobCache.get(ref);
  };
  const prefetchPreAnswer = (item) => {
    const segments = item?.segments || {};
    [...(segments.stem || []), ...(segments.options || [])].forEach((ref) => {
      fetchSegment(ref).catch(() => {});
    });
  };
  const prepareQuestion = async (item) => {
    if (!item || item.segments) return item;
    if (!prepared.has(item.questionId)) {
      prepared.set(item.questionId, Promise.resolve(api.getAudioManifest(item.questionId))
        .then((manifest) => manifestToQuestion(item, manifest)));
    }
    return prepared.get(item.questionId);
  };
  // Retain the NEXT question's stem/options refs (and prefetch them) so the
  // current cache-eviction keeps what the next stem will need.
  const retainNext = (retainedRefs) => (next) => {
    [...(next?.segments?.stem || []), ...(next?.segments?.options || [])]
      .forEach((ref) => retainedRefs.add(ref));
    prefetchPreAnswer(next);
  };
  const next = async () => {
    while (index < questions.length) {
      const raw = questions[index];
      index += 1;
      const retainedIds = new Set([raw?.questionId, questions[index]?.questionId]);
      prepared.forEach((_, id) => {
        if (!retainedIds.has(id)) prepared.delete(id);
      });
      // Begin only the next manifest while resolving current. A denied or absent
      // manifest skips that queue item without wedging the session.
      if (questions[index]) prepareQuestion(questions[index]).catch(() => {});
      try {
        const value = await prepareQuestion(raw);
        // Bounded current + next pre-answer fetch. Answer/deeper untouched;
        // next-manifest latency never delays current stem playback.
        prefetchPreAnswer(value);
        const retainedRefs = new Set([...Object.values(value.segments || {}).flat()]);
        if (questions[index]) {
          prepareQuestion(questions[index]).then(retainNext(retainedRefs)).catch(() => {});
        }
        blobCache.forEach((_, ref) => {
          if (!retainedRefs.has(ref)) blobCache.delete(ref);
        });
        return { value, done: false };
      } catch (error) {
        // Fail open to the next audio-eligible queue item.
      }
    }
    return { value: undefined, done: true };
  };
  const dispose = () => {
    blobCache.clear();
    prepared.clear();
  };
  return { next, fetchSegment, dispose };
}

/** Dark, dependency-injected Drive Mode surface. Questions use the machine's
 * ordered segment-list shape; submitAnswer is the existing grading-path seam. */
export default function DriveModeScreen({ questions = [], submitAnswer, onSessionEnd, onComplete, onExit, sync, advanceMs = 3000, resolveRoutePlugin = resolveDriveRoutePlugin }) {
  const enabled = driveModeAvailable();
  const audioRef = useRef(null);
  const earconAudioRef = useRef(null);
  const playerRef = useRef(null);
  const answerGuardRef = useRef(false);
  // S2/D4 — stable per-row UI selection identity: records WHICH row was tapped
  // ({questionId, rowId}) so duplicate/case-colliding display letters can't both
  // mark chosen. The graded payload still carries the selected real option string.
  const pickIdentityRef = useRef(null);
  const wakeLockRef = useRef(null);
  const completedRef = useRef(false);
  const [snapshot, setSnapshot] = useState(INITIAL_STATE);

  useEffect(() => {
    if (!enabled || !audioRef.current) return undefined;
    let disposed = false;
    const queue = createDriveModeQueue(questions);
    const engine = createDriveModeEngine({ audioElement: audioRef.current, fetchSegment: queue.fetchSegment });
    // Route-loss hold, tracked from the player snapshot. While held, earcons are
    // suppressed (pausing only the speech element would still let the earcon play
    // on the fallback speaker).
    let held = false;
    const playEarcon = (assetUrl) => {
      if (held) return Promise.resolve();
      const earcon = earconAudioRef.current;
      if (!earcon) return Promise.resolve();
      earcon.src = assetUrl;
      return Promise.resolve(earcon.play());
    };
    let previousState = null;
    const player = createDriveModePlayer({
      engine,
      queue,
      submitAnswer: submitAnswer || (() => Promise.resolve()),
      config: { advanceMs },
      onReprompt: () => playEarcon(repromptEarcon).catch(() => {}),
      onState: (next) => {
        if (disposed) return;
        held = next.routeHeld === true;
        answerGuardRef.current = next.state !== STATES.AWAITING_ANSWER;
        setSnapshot(next);
        if (next.state === STATES.AWAITING_ANSWER && previousState !== STATES.AWAITING_ANSWER) {
          answerGuardRef.current = false;
          playEarcon(answerWindowEarcon).catch(() => {});
        }
        previousState = next.state;
      },
    });
    playerRef.current = player;

    // Route-change handler: an `oldDeviceUnavailable:true` event latches a
    // no-auto-resume hold that synchronously pauses speech BEFORE the listener
    // returns and silences the earcon sink; `false` is a no-op.
    const handleRouteChange = (event) => {
      if (disposed) return;
      if (event && event.oldDeviceUnavailable === true) {
        held = true;
        player.routeHold();
        const earcon = earconAudioRef.current;
        if (earcon && typeof earcon.pause === "function") earcon.pause();
      }
    };

    // Native order is addListener -> configure -> start. On web / non-native the
    // adapter makes ZERO plugin calls and we start synchronously (existing
    // behavior preserved). A native plugin setup failure safely disables
    // playback: the player is never started, so no segment is fetched or played.
    let routeAdapter = { remove() {} };
    // Cancellation ownership for a still-pending native bind: if we unmount while
    // configure() is unsettled, cancelRouteBind() removes the already-attached
    // listener exactly once even though the bind promise may never resolve. Kept
    // as a plain token + free function (no `this`, which is invalid in a
    // functional component).
    const routeCancellation = { cancelled: false, onCancel: null };
    const cancelRouteBind = () => {
      routeCancellation.cancelled = true;
      if (routeCancellation.onCancel) routeCancellation.onCancel();
    };
    const plugin = resolveRoutePlugin();
    if (plugin) {
      // Native runtime WITH the plugin: addListener -> configure -> start.
      bindDriveRouteChange({ plugin, onRouteChange: handleRouteChange, cancellation: routeCancellation }).then((adapter) => {
        routeAdapter = adapter;
        if (disposed) {
          adapter.remove();
          return;
        }
        if (adapter.active) player.start();
      });
    } else if (!isNativeCapacitorRuntime()) {
      // Web / non-native: zero plugin calls, start synchronously (byte-preserved).
      // A native Capacitor shell whose QlearnAudioSession plugin is absent or
      // malformed falls through here and safe-disables: the player is never
      // started (no segment fetch/play, no listener), leaving the quiz path intact.
      player.start();
    }

    return () => {
      disposed = true;
      cancelRouteBind();
      routeAdapter.remove();
      onSessionEnd?.();
      player.destroy();
      engine.destroy();
      queue.dispose();
      playerRef.current = null;
    };
  }, [enabled, questions, submitAnswer, onSessionEnd, advanceMs, resolveRoutePlugin]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (snapshot.state === STATES.AWAITING_ANSWER && navigator.wakeLock?.request) {
      Promise.resolve(navigator.wakeLock.request("screen"))
        .then((lock) => { wakeLockRef.current = lock; })
        .catch(() => {});
    } else if (wakeLockRef.current) {
      wakeLockRef.current.release?.().catch?.(() => {});
      wakeLockRef.current = null;
    }
    return undefined;
  }, [enabled, snapshot.state]);

  useEffect(() => () => { wakeLockRef.current?.release?.(); }, []);
  useEffect(() => {
    if (snapshot.state === STATES.ENDED && !completedRef.current) {
      completedRef.current = true;
      Promise.resolve(onSessionEnd?.()).then(onComplete);
    }
  }, [snapshot.state, onComplete, onSessionEnd]);

  if (!enabled) return null;
  const current = questions.find((item) => item.questionId === snapshot.questionId);
  const options = Array.isArray(current?.options) ? current.options : [];
  // S2/D5 — correctness is VALUE-based: ONE shared real-string normalizer drives
  // isCorrectValue() against the threaded correctAnswer STRING, exactly like
  // exam-question-container (NOT the synthesized A/B/C display letter).
  const normalizeReal = (text) => String(text ?? "").trim().toLowerCase();
  const normalizedCorrect = normalizeReal(current?.correctAnswer);
  // S2/D6 — ground truth exists only when a non-empty correctAnswer was threaded;
  // without it correctness is UNKNOWN and must never be asserted as a grade.
  const hasCorrectAnswer = normalizedCorrect.length > 0;
  const isCorrectValue = (value) => hasCorrectAnswer && normalizeReal(value) === normalizedCorrect;
  // S2/D4 — chosenness is IDENTITY-based: the opaque rowId the student tapped for
  // the CURRENT question, so duplicate/case-colliding display letters resolve to
  // exactly one chosen row.
  const pickedRowId = pickIdentityRef.current && pickIdentityRef.current.questionId === snapshot.questionId
    ? pickIdentityRef.current.rowId
    : null;
  // `value` is the real production option string threaded to the grading seam
  // (submitAnswer -> recordPick -> buildSessionPayload -> answerGiven); `rowId` is
  // the opaque per-row identity, retained ONLY as the stable UI selection identity
  // — it never enters the payload path.
  const answer = (value, rowId) => {
    if (answerGuardRef.current || snapshot.state !== STATES.AWAITING_ANSWER) return;
    answerGuardRef.current = true;
    pickIdentityRef.current = { questionId: snapshot.questionId, rowId };
    playerRef.current?.answer(value);
  };
  const remote = (command) => playerRef.current?.remoteCommand(command);
  const bannerText = phaseBanner(snapshot);

  return (
    // User-facing name is "Audio Mode" ("Drive" carries legal driving implications).
    // The internal name stays "drive"/"Drive Mode" everywhere (component, route
    // "/drive", flags, test ids, telemetry) — a deliberate internal/external split
    // so this tranche adds no cross-cutting rename churn.
    <main className="drive-mode-screen" data-testid="drive-mode-screen">
      <audio ref={audioRef} data-testid="drive-mode-audio" />
      <audio ref={earconAudioRef} data-testid="drive-mode-earcon-audio" />
      <header className="drive-mode-screen__header">
        <span>Audio Mode</span>
        <span>{snapshot.questionId ? `Question ${snapshot.questionIndex + 1} of ${questions.length}` : "Loading"}</span>
      </header>
      <p className="drive-mode-screen__banner" data-testid="drive-mode-banner" aria-live="polite">{bannerText}</p>
      {/* data-hj-suppress: read-along renders the stem, the full option list and
          the explanation verbatim — identical in kind to the exam question
          wrapper. The existing GTM/FB route guard does NOT cover this surface
          either: gl-route.js lists "/drive-mode" but the real route is "/drive"
          (routes.jsx), so this attribute is the only control here. */}
      <section className="drive-mode-screen__content" data-hj-suppress>
        {/* Read-along, phase-appropriate: text is rendered ONLY when the metadata
            carried it, so a question without a stem/explanation shows no empty node. */}
        {snapshot.state === STATES.PLAYING_STEM && current?.stem && (
          <p className="drive-mode-screen__stem" data-testid="drive-mode-stem">{current.stem}</p>
        )}
        {snapshot.state === STATES.PLAYING_OPTIONS && (
          <ul className="drive-mode-screen__options" data-testid="drive-mode-options" aria-label="Answer choices">
            {options.map((option, index) => {
              const key = optionKey(option, index);
              // S2/D3 — React key + test-id use the opaque rowId, never the display
              // letter (the same latent aliasing hazard the answer grid had). The
              // screen-side `?? r${index}` fallback only serves direct-screen
              // fixtures that bypass toQueue; it is also ordinal, so never aliases.
              const rowId = option.rowId ?? `r${index}`;
              return (
                <li key={rowId} data-testid={`drive-option-${rowId}`}>
                  <strong>{key}</strong><span>{optionValue(option)}</span>
                </li>
              );
            })}
          </ul>
        )}
        {snapshot.state === STATES.AWAITING_ANSWER && (
          <section className="drive-mode-screen__answers" aria-label="Choose an answer">
            {options.map((option, index) => {
              const key = optionKey(option, index);
              const value = optionValue(option);
              // S2/D3 — identity is the opaque rowId; the display letter stays the
              // visible <strong>, the payload stays the real option string.
              const rowId = option.rowId ?? `r${index}`;
              return (
                <button key={rowId} type="button" data-testid={`drive-answer-${rowId}`} onClick={() => answer(value, rowId)}>
                  <strong>{key}</strong><span>{value}</span>
                </button>
              );
            })}
          </section>
        )}
        {/* S2 tutor marking (D5/D6): the full options list stays visible through
            the answer phase; the tapped row is marked ✓ (correct) / ✗ (wrong) and
            the correct option highlighted — driven by the stable per-row pick
            identity + threaded metadata, NEUTRAL when correctness is unknown. */}
        {(snapshot.state === STATES.PLAYING_ANSWER || snapshot.state === STATES.PLAYING_DEEPER) && (
          <>
            {options.length > 0 && (
              <ul className="drive-mode-screen__marked-options" data-testid="drive-mode-marked-options" aria-label="Answer review">
                {options.map((option, index) => {
                  const key = optionKey(option, index);
                  const value = optionValue(option);
                  const rowId = option.rowId ?? `r${index}`;
                  // Chosen is IDENTITY-based (opaque rowId) — NOT raw string
                  // equality — so two rows with the same display string never both
                  // mark chosen (D4).
                  const chosen = pickedRowId !== null && rowId === pickedRowId;
                  const correct = isCorrectValue(value);
                  // Without ground truth the chosen row is NEUTRAL — never asserted
                  // correct or incorrect (D6).
                  const mark = chosen
                    ? (hasCorrectAnswer ? (correct ? "correct" : "wrong") : "chosen")
                    : (correct ? "correct-answer" : "neutral");
                  return (
                    <li key={rowId} data-testid={`drive-marked-${rowId}`} data-mark={mark} className={`drive-mode-screen__marked-option is-${mark}`}>
                      <strong>{key}</strong><span>{value}</span>
                      {chosen && hasCorrectAnswer && (
                        <span className="drive-mode-screen__mark" data-testid={`drive-mark-${rowId}`} aria-label={correct ? "Correct" : "Incorrect"}>{correct ? "✓" : "✗"}</span>
                      )}
                      {!chosen && correct && (
                        <span className="drive-mode-screen__mark" aria-label="Correct answer">✓</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {current?.explanation && (
              <p className="drive-mode-screen__explanation" data-testid="drive-mode-explanation">{current.explanation}</p>
            )}
          </>
        )}
        {snapshot.state === STATES.ENDED && sync?.status !== "failed" && <p className="drive-mode-screen__ended">Session complete</p>}
        {snapshot.state === STATES.ENDED && sync?.status === "synced" && (
          <p className="drive-mode-screen__summary">{sync.result?.quizStatus || "Graded summary ready"}</p>
        )}
        {sync?.status === "failed" && <p role="alert">Couldn't sync your session — your answers were not saved.</p>}
      </section>
      <nav className="drive-mode-screen__controls" aria-label="Audio Mode controls">
        <div className="drive-mode-screen__controls-primary">
          <button type="button" onClick={() => remote(REMOTE_COMMANDS.PLAY_PAUSE)}>Play / Pause</button>
          <button type="button" onClick={() => remote(REMOTE_COMMANDS.NEXT)}>Skip</button>
          <button type="button" onClick={() => remote(REMOTE_COMMANDS.PREV)}>Replay stem</button>
          <button type="button" onClick={() => playerRef.current?.deeper()}>Deeper</button>
        </div>
        {/* Exit is on its own row with secondary/destructive styling, out of the
            Deeper mis-tap zone. */}
        {onExit && (
          <button type="button" className="drive-mode-screen__exit" data-testid="drive-exit" onClick={onExit}>Exit</button>
        )}
      </nav>
    </main>
  );
}
